import { SEO_TITLE_WIKI_article } from 'mk/autogenerated/translations/ArticlePhotoPage.10d25cc270d33f4811f4dcb37f34babc'
import { articleFetchTrigger } from 'mk2/apps/wiki/containers/Article/Article.actions';
import articlesReducer from 'mk2/apps/wiki/containers/Article/Article.reducers';
import articlesSaga from 'mk2/apps/wiki/containers/Article/Article.sagas';
import { getWikiArticleState } from 'mk2/apps/wiki/containers/Article/Article.selectors';
import ArticlePhoto from 'mk2/apps/wiki/containers/ArticlePhoto/ArticlePhoto';
import { isArticleOfReviewsCategory } from 'mk2/apps/wiki/helpers';
import { wikiArticleUrl } from 'mk2/apps/wiki/urls';
import { AppName } from 'mk2/containers/Application/Application';
import { getFirstParagraphsPlaintext } from 'mk2/helpers/article_ast/utils';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import Page, { HalfpageProps, RightContentSide } from 'mk2/pages/Page';
import { getRequestDeviceMobile, getRequestPermissions, getRequestUser, AppState } from 'mk2/reducers';
import { LoadingState } from 'mk2/reducers/loadingState';
import { ArticleConceptPath, MenuSelection, UserEntity, WikiArticleEntity, WikiArticleSchema } from 'mk2/schemas';
import { getDenormalizedEntity } from 'mk2/selectors';
import { interpolate } from 'mk2/services/i18n';
import { Store } from 'mk2/store/configureStore';
import React from 'react';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps } from 'react-router';

export interface OwnProps {}

export interface StateProps {
    article: WikiArticleEntity;
    articleConceptPath: ArticleConceptPath[];
    photoId: number;
    errorMessage: string;
    loadingState: LoadingState;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    isMobile: boolean;
}

export interface DispatchProps {
    onLoad(articleSlug: string, categorySlug: string);
}

export type RouteProps = RouteComponentProps<{
    categorySlug: string;
    articleSlug: string;
    photoId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ArticlePhotoPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                match: { params },
            } = ownProps;
            const categorySlug = params.categorySlug;
            const articleSlug = params.articleSlug;
            const photoId = parseInt(params.photoId, 10);

            const articleState = getWikiArticleState(state, categorySlug, articleSlug);
            const article: WikiArticleEntity = getDenormalizedEntity<WikiArticleEntity>(state, WikiArticleSchema, articleState.articleId);

            return {
                article,
                articleConceptPath: articleState.articleConceptPath,
                photoId,
                isMobile: getRequestDeviceMobile(state),
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                loadingState: articleState.loadingState,
                errorMessage: articleState.errorMessage,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: articleFetchTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { article } = props;
        if (article) {
            // custom napisany SEO title, alebo to iste ako nadpis clanku
            return article.seoTitle || interpolate(SEO_TITLE_WIKI_article, { article: article.title });
        } else {
            return null;
        }
    }

    public getMetaDescription(props: Props): string {
        const { article } = props;
        if (article) {
            // custom napisany SEO description, alebo to iste ako prvy odstavec clanku
            return article.seoDescription || getFirstParagraphsPlaintext(article.bodyAST);
        } else {
            // pouzi defaultny description v Page
            return null;
        }
    }

    public getMetaFaqRichSnippet(props: Props): string {
        const { article } = props;
        return article?.seoFaqRichSnippet;
    }

    public render(props: Props): false | JSX.Element {
        return <ArticlePhoto {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public getHeaderTitle(props: Props): string {
        const { article } = props;
        return article ? interpolate(SEO_TITLE_WIKI_article, { article: article.title }) : null;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const {
            match: {
                params: { categorySlug, articleSlug },
            },
        } = props;
        return url(wikiArticleUrl, { categorySlug, articleSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        const { article } = props;
        if (article && !article.disabledAds && !isArticleOfReviewsCategory(article.category.slug)) {
            return {
                zone: 'Wiki - Halfpage Sticky (Desktop)',
            };
        }
        return null;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: app,
            activeItem: props.article ? props.article.slug : null,
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.wiki.article', articlesReducer);
        store.injectSaga('containers.wiki.article', articlesSaga);
    }
}
